/**
 * Tolerant socket payload decoding for the realtime-contract migration
 * (docs/realtime-sync-audit.md §4.1 / §5 Phase 0 item 3).
 *
 * Accepts every encoding the server has ever used:
 *  - JSON string          (legacy `JSON.stringify(...)` emit sites)
 *  - plain object         (legacy raw-object emit sites)
 *  - versioned envelope   `{ v: 1, event, data, ... }` (Phase 1+ servers)
 *
 * Handlers must treat `data: undefined` as "drop the event" — never throw.
 */

export interface RealtimeMeta {
  v: number;
  event: string;
  workspaceId?: number | null;
  seq?: number | null;
  actorUserId?: number | null;
  actorSocketId?: string | null;
}

export interface DecodedPayload<T> {
  data: T | undefined;
  meta: RealtimeMeta | null;
}

export function decodeSocketPayload<T>(raw: unknown): DecodedPayload<T> {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return { data: undefined, meta: null };
    }
  }
  if (value === null || value === undefined) {
    return { data: undefined, meta: null };
  }
  if (typeof value === 'object' && (value as RealtimeMeta).v === 1 && 'data' in value && 'event' in value) {
    const envelope = value as RealtimeMeta & { data: T };
    return { data: envelope.data, meta: envelope };
  }
  return { data: value as T, meta: null };
}
